import * as React from 'react'
import { useLocation } from 'react-router-dom'

import { useAiChat } from '@factorialco/f0-react'
import { useCoAgent, useCopilotChat } from '@copilotkit/react-core'

import { useOneCreateActionState } from 'modules/core/lib/oneCreateActions'

import { AiAgentGlobalActions } from 'modules/ai/components/AiAgentGlobalActions'

export type OneSharedState<T = unknown> = {
  platform: 'web' | 'mobile'
  requiredSkills: string[]
  coCreationData: T | null
  files?: OneFile[] | null
  currentPage: {
    url: string
    title: string
  }
}

export type OneFile = {
  type: 'binary'
  url?: string
  filename?: string
  mimeType?: string
}

type OneContextValue = {
  state: OneSharedState
  setState: (data: OneSharedState) => void
  isThinking: boolean
}

const OneContext = React.createContext<OneContextValue | null>(null)
const OneAvailableContext = React.createContext<boolean>(false)

export const useOneFeatureAvailable = () => React.useContext(OneAvailableContext)

const DisabledOneProvider = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation()

  // Build full URL from React Router's location so we stay in sync with the router (avoids
  // window.location timing issues). Origin is from window so we still get a full href.
  const currentPageFromLocation = React.useMemo(
    () => ({
      url: `${window.location.origin}${location.pathname}${location.search}${location.hash ?? ''}`,
      title: document.title,
    }),
    [location.pathname, location.search, location.hash]
  )

  const [state, setState] = React.useState<OneSharedState>(() => ({
    platform: 'web' as const,
    requiredSkills: [],
    coCreationData: null,
    files: null,
    currentPage: currentPageFromLocation,
  }))

  // Keep state.currentPage in sync with the router. When the URL changes, also reset
  // coCreationData and requiredSkills so consumers (e.g. useOne) re-run their init effect
  // and set fresh state for the new page (e.g. a different form's questionnaire).
  React.useEffect(() => {
    setState((prev) => {
      const urlOrTitleChanged =
        prev.currentPage.url !== currentPageFromLocation.url ||
        prev.currentPage.title !== currentPageFromLocation.title
      if (!urlOrTitleChanged) return prev
      return {
        ...prev,
        currentPage: currentPageFromLocation,
        coCreationData: null,
        files: null,
        requiredSkills: [],
      }
    })
  }, [currentPageFromLocation, setState])

  const value = React.useMemo<OneContextValue>(() => {
    return { state, setState, isThinking: false }
  }, [state, setState])

  return <OneContext.Provider value={value}>{children}</OneContext.Provider>
}

const InternalOneProvider = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation()
  const { setInitialMessage, setFooter, setPlaceholders, setVisualizationMode } = useAiChat()
  const initialState = React.useMemo<OneSharedState>(() => {
    return {
      platform: 'web',
      requiredSkills: [],
      coCreationData: null,
      files: null,
      currentPage: {
        url: window.location.href,
        title: document.title,
      },
    }
  }, [])

  const { state, setState } = useCoAgent<OneSharedState>({
    name: 'one-workflow',
    initialState,
  })

  const { isLoading: isThinking } = useCopilotChat()

  // Always keep current page in sync. Do not apply any change when the URL is an activity (hash).
  // When the URL changes, reset co-creation state, restore the default initial message, clear the footer and placeholders, and return to sidepanel.
  React.useEffect(() => {
    const nextPage = { url: window.location.href, title: document.title }
    if (window.location.hash) return
    if (state.currentPage.url === nextPage.url && state.currentPage.title === nextPage.title) {
      return
    }
    if (state.currentPage.url !== nextPage.url) {
      setInitialMessage(undefined)
      setFooter(undefined)
      setPlaceholders([])
      setVisualizationMode('sidepanel')
      setState({
        ...state,
        currentPage: nextPage,
        coCreationData: null,
        files: null,
        requiredSkills: [],
      })
    } else {
      setState({ ...state, currentPage: nextPage })
    }
  }, [
    location.key,
    setInitialMessage,
    setFooter,
    setPlaceholders,
    setVisualizationMode,
    setState,
    state,
  ])

  const value = React.useMemo<OneContextValue>(() => {
    return { state, setState, isThinking }
  }, [state, setState, isThinking])

  return <OneContext.Provider value={value}>{children}</OneContext.Provider>
}

type UseOneResult<T> = {
  coCreationData: T
  setCoCreationData: (data: T) => void
  isThinking: boolean
}

export type UseOneOptions = {
  /** When true, opens the AI chat panel when the component mounts. Only has effect when used inside the layout that provides the chat (e.g. FactorialOneLayout). */
  autoOpenChat?: boolean
  /** When set, this message is shown as the initial user message in the chat when the panel opens (e.g. when using autoOpenChat). */
  initialMessage?: string
  /** When true, opens the chat in fullscreen mode (uses setVisualizationMode). Only has effect when autoOpenChat is true. */
  fullscreen?: boolean
  /** Optional footer content (e.g. action buttons) rendered below the chat textarea. Cleared on URL change. */
  footer?: React.ReactNode
  /** Optional placeholder strings shown as suggestions in the chat input. Cleared on URL change. */
  placeholders?: string[]
  /** Optional files passed to One alongside co-creation data. */
  files?: OneFile[] | null
  /** Stable key so the effect re-runs when footer intent changes (e.g. pass `canCreateSurvey`). Avoids listing `footer` in deps and prevents update loops. */
  footerKey?: string | number | boolean
  /** Stable key so the effect re-runs when placeholders intent changes (e.g. pass `placeholders.length`). */
  placeholdersKey?: string | number
}

export const useOne = <T,>(
  requiredSkills: string[],
  coCreationData: T,
  options?: UseOneOptions
): UseOneResult<T> => {
  const ctx = React.useContext(OneContext)
  if (!ctx) {
    throw new Error('useOne must be used within OneProvider')
  }

  const { setOpen: setChatOpen, setInitialMessage, setVisualizationMode, setFooter } = useAiChat()
  const { data: dataFromRedirect } = useOneCreateActionState<T>()

  // footerDep/placeholdersDep used instead of options?.footer/placeholders to avoid update
  // loops when footer/placeholders get new refs each render; pass footerKey/placeholdersKey for stability.
  React.useEffect(() => {
    if (options?.autoOpenChat) {
      setVisualizationMode(options.fullscreen ? 'fullscreen' : 'sidepanel')
      if (options.initialMessage !== undefined) {
        setInitialMessage(options.initialMessage)
      }
      setChatOpen(true)
    }
  }, [
    options?.autoOpenChat,
    options?.fullscreen,
    options?.initialMessage,
    setChatOpen,
    setInitialMessage,
    setVisualizationMode,
  ])

  React.useEffect(() => {
    setFooter(options?.footer ?? undefined)
  }, [setFooter, options?.footer])

  React.useEffect(() => {
    if (ctx.state.coCreationData !== null) return

    const newCoCreationData = dataFromRedirect ?? coCreationData
    const initialState: OneSharedState<T> = {
      platform: 'web' as const,
      requiredSkills,
      coCreationData: newCoCreationData,
      files: options?.files ?? null,
      currentPage: { url: window.location.href, title: document.title },
    }

    ctx.setState(initialState)
  }, [ctx, coCreationData, requiredSkills, dataFromRedirect, options?.files])

  React.useEffect(() => {
    const nextFiles = options?.files ?? null

    if (ctx.state.files === nextFiles) return

    ctx.setState({ ...ctx.state, files: nextFiles })
  }, [ctx, options?.files])

  const setCoCreationData = React.useCallback(
    (data: T) => {
      ctx.setState({ ...ctx.state, coCreationData: data })
    },
    [ctx]
  )

  return {
    coCreationData: ctx.state.coCreationData as T,
    setCoCreationData: setCoCreationData,
    isThinking: ctx.isThinking,
  }
}

export const OneProvider = ({
  children,
  enabled,
  available = false,
}: {
  children: React.ReactNode
  enabled: boolean
  available?: boolean
}) => {
  const inner = enabled ? (
    <InternalOneProvider>
      <AiAgentGlobalActions />
      {children}
    </InternalOneProvider>
  ) : (
    <DisabledOneProvider>{children}</DisabledOneProvider>
  )

  return <OneAvailableContext.Provider value={available}>{inner}</OneAvailableContext.Provider>
}
